import React, { ComponentType, useState } from 'react';
import {
  Box,
  HStack,
  Icon,
  IconProps,
  IconButton,
  Text,
  Textarea,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { FiSend } from 'react-icons/fi';
import { sendInstagramMessage } from '../../../hooks/instagram';

interface ComposerProps {
  conversationId: string;
  orgId: string;
  recipient_phone: string;
  onSent?: (m: any) => void;
}

export default function InstagramComposer({
  conversationId,
  orgId,
  onSent,
}: ComposerProps) {
  const [text, setText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const toast = useToast();

  const send = async () => {
    if (!text.trim()) {
      toast({
        title: 'Message cannot be empty',
        status: 'warning',
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    setIsSending(true);
    const optimisticMessage = {
      id: `tmp-${Date.now()}`,
      sender: 'agent',
      direction: 'outbound',
      text: text.trim(),
      whatsapp_message_id: null,
      status: 'pending',
      error_code: null,
      raw_payload: {},
      timestamp: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      is_deleted: false,
    };

    try {
      const response = await sendInstagramMessage(orgId, conversationId, text.trim());
      if (!response.success) {
        throw new Error('Message send failed');
      }

      onSent?.(response.data || optimisticMessage);
      setText('');
    } catch (error: any) {
      toast({
        title: 'Failed to send message',
        description: error?.response?.data?.detail || 'Please try again.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Box
      p={2}
      bg="#F9FAFB"
      borderRadius="24px"
      border="1px solid"
      borderColor="gray.200"
    >
      <HStack align="flex-end" spacing={3}>
        <VStack flex="1" align="stretch" spacing={1}>
          <Textarea
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder="Message"
            resize="none"
            minH="44px"
            maxH="160px"
            rows={1}
            bg="white"
            border="0"
            px={4}
            py={3}
            _focus={{
              boxShadow: 'none',
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                send();
              }
            }}
          />
          <Text fontSize="xs" color="gray.500" px={4}>
            Press Enter to send. Shift + Enter for a new line.
          </Text>
        </VStack>
        <IconButton
          aria-label="Send Instagram message"
          icon={<Icon as={FiSend as ComponentType<IconProps>} />}
          colorScheme="pink"
          borderRadius="full"
          alignSelf="flex-end"
          isLoading={isSending}
          isDisabled={!text.trim()}
          onClick={send}
        />
      </HStack>
    </Box>
  );
}
